import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CornerDownLeft, Eye, EyeOff } from "lucide-react";
import { useAuth } from "../../../context/AuthContext";
import { forgotPassword } from "../../../api/services/authService";
import AuthTerminalModal from "./AuthTerminalModal";
import { PromptField, Trace, TraceLine } from "./TerminalParts";
import {
  buttonClass,
  ghostButtonClass,
  inputClass,
  useSequence,
} from "./terminalUi";

const BOOT_STEPS = ["resolving range endpoint", "tls pinned · cert 9f2a…c41"];
const BOOT_INTERVAL_MS = 260;

const AUTH_STEPS = [
  "validating fields",
  "contacting identity provider",
  "issuing session token",
];
const AUTH_INTERVAL_MS = 600;

/* Enough for the last `ok` to paint, not a pause. */
const DONE_HOLD_MS = 200;

const STATUS = {
  idle: "session live",
  running: "authenticating",
  done: "session ready",
};

/* Sign-in as a shell session, so the visitor never leaves the landing page.
 * Same endpoints as /login, via AuthContext. */
export default function LoginTerminal({ onClose, onSwitchToSignup }) {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [form, setForm] = useState({ username: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [capsLockOn, setCapsLockOn] = useState(false);
  const [error, setError] = useState("");

  /* "login" | "reset" */
  const [mode, setMode] = useState("login");
  /* "idle" → form, "running" → trace, "done" → handing off to /home */
  const [phase, setPhase] = useState("idle");
  /* Request came back ok; the trace may still have lines to narrate. */
  const [authorized, setAuthorized] = useState(false);

  const [resetEmail, setResetEmail] = useState("");
  const [resetState, setResetState] = useState({ sending: false, message: "" });

  const bootStep = useSequence(BOOT_STEPS.length, BOOT_INTERVAL_MS);
  const authStep = useSequence(
    AUTH_STEPS.length,
    AUTH_INTERVAL_MS,
    phase === "running",
  );
  const finished = phase === "done";

  // Keycloak answers faster than the trace reads, so success waits for the last
  // line. Failures aren't delayed — an error interrupts the trace.
  useEffect(() => {
    if (phase !== "running" || !authorized) return;
    if (authStep < AUTH_STEPS.length * 2 - 2) return;
    setPhase("done");
  }, [phase, authorized, authStep]);

  useEffect(() => {
    if (!finished) return;
    const id = setTimeout(() => navigate("/home"), DONE_HOLD_MS);
    return () => clearTimeout(id);
  }, [finished, navigate]);

  const handleChange = (event) => {
    setForm({ ...form, [event.target.name]: event.target.value });
    setError("");
  };

  const trackCapsLock = (event) => {
    setCapsLockOn(event.getModifierState?.("CapsLock") ?? false);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setAuthorized(false);
    setPhase("running");

    const result = await login(form.username, form.password, "other");

    if (!result.ok) {
      setPhase("idle");
      setError(result.errorMessage || "Invalid username or password");
      return;
    }
    setAuthorized(true);
  };

  const handleResetSubmit = async (event) => {
    event.preventDefault();
    setResetState({ sending: true, message: "" });
    try {
      const response = await forgotPassword(resetEmail);
      setResetState({
        sending: false,
        message: response.message || "Reset link sent if the account exists.",
      });
    } catch (resetError) {
      setResetState({
        sending: false,
        message: resetError.message || "Could not send the reset link.",
      });
    }
  };

  const openReset = () => {
    setMode("reset");
    setError("");
    const typed = form.username.trim();
    if (typed.includes("@") && !resetEmail) setResetEmail(typed);
  };

  return (
    <AuthTerminalModal
      onClose={onClose}
      labelledBy="lp-auth-command"
      status={STATUS[phase]}
    >
      <p id="lp-auth-command" className="qv-mono text-[12.5px]">
        <span className="text-(--color-primary)">root@quantum-vanguard</span>
        <span className="text-(--lp-term-dim)">:~#</span>{" "}
        <span className="text-(--lp-term-fg)">
          qv auth {mode === "reset" ? "--reset" : "--login"}
        </span>
      </p>

      <div className="qv-mono mt-3 space-y-1.5">
        {BOOT_STEPS.map((label, index) =>
          bootStep > index * 2 ? (
            <TraceLine
              key={label}
              label={label}
              done={bootStep > index * 2 + 1}
            />
          ) : null,
        )}
      </div>

      {mode === "reset" ? (
        <form onSubmit={handleResetSubmit} className="qv-mono mt-6 space-y-5">
          <p className="text-[12.5px] leading-relaxed text-(--lp-term-dim)">
            Enter the email address.
          </p>

          <PromptField label="account_email">
            <input
              type="email"
              value={resetEmail}
              onChange={(event) => setResetEmail(event.target.value)}
              placeholder="you@company.com"
              className={inputClass}
              required
              autoFocus
            />
          </PromptField>

          {resetState.message && (
            <p className="lp-term-line text-[12px] text-(--color-primary)">
              ▸ {resetState.message}
            </p>
          )}

          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => setMode("login")}
              className={ghostButtonClass}
            >
              ← back
            </button>
            <button
              type="submit"
              disabled={!resetEmail || resetState.sending}
              className={buttonClass}
            >
              <CornerDownLeft className="h-4 w-4" />
              {resetState.sending ? "sending…" : "send reset link"}
            </button>
          </div>
        </form>
      ) : phase === "idle" ? (
        <>
          <div className="qv-mono mt-6 flex flex-wrap items-center gap-3">
            <span className="text-[11.5px] text-(--lp-term-dim)">mode:</span>
            <span className="rounded-md border border-(--color-primary) bg-[color-mix(in_srgb,var(--color-primary)_10%,transparent)] px-4 py-1.5 text-[12.5px] text-(--color-primary)">
              [ login ]
            </span>
            <button
              type="button"
              onClick={onSwitchToSignup}
              className="lp-term-rule rounded-md border border-transparent px-4 py-1.5 text-[12.5px] text-(--lp-term-dim) transition-colors hover:border-(--color-primary) hover:text-(--color-primary)"
            >
              [ signup ]
            </button>
          </div>

          <form onSubmit={handleSubmit} className="qv-mono mt-6 space-y-6">
            <PromptField
              label="username_or_email"
              trailing={
                <span className="text-[11px] text-(--lp-term-dim)">⏎</span>
              }
            >
              <input
                type="text"
                name="username"
                value={form.username}
                onChange={handleChange}
                placeholder="username or email"
                autoComplete="username"
                className={inputClass}
                required
                autoFocus
              />
            </PromptField>

            <PromptField
              label="password"
              trailing={
                <button
                  type="button"
                  onClick={() => setShowPassword((visible) => !visible)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  className="text-(--lp-term-dim) transition-colors hover:text-(--color-primary)"
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              }
            >
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                value={form.password}
                onChange={handleChange}
                onKeyDown={trackCapsLock}
                onKeyUp={trackCapsLock}
                placeholder="••••••••"
                autoComplete="current-password"
                className={inputClass}
                required
              />
            </PromptField>

            {capsLockOn && (
              <p className="lp-term-line -mt-3 text-[11px] text-[#ef9f27]">
                ▸ caps lock is on
              </p>
            )}

            <div className="flex justify-end">
              <button
                type="button"
                onClick={openReset}
                className="text-[12px] text-(--color-primary) transition-opacity hover:opacity-80"
              >
                # qv auth --reset
              </button>
            </div>

            {error && (
              <p className="lp-term-line text-[12px] text-(--lp-term-err)">
                ▸ error: {error}
              </p>
            )}

            <div className="flex gap-3">
              <button type="submit" className={buttonClass}>
                <CornerDownLeft className="h-4 w-4" />
                authenticate
              </button>
              <button
                type="button"
                onClick={onClose}
                className={ghostButtonClass}
              >
                esc
              </button>
            </div>
          </form>

          <p className="qv-mono mt-5 text-center text-[12px] text-(--lp-term-dim)">
            no account yet?{" "}
            <button
              type="button"
              onClick={onSwitchToSignup}
              className="text-(--color-primary) transition-opacity hover:opacity-80"
            >
              qv auth --signup
            </button>
          </p>
        </>
      ) : (
        <div className="mt-4">
          <Trace steps={AUTH_STEPS} step={authStep} finished={finished} />
        </div>
      )}
    </AuthTerminalModal>
  );
}
